/**
 * Typed wrappers around the `/api/me/companies/*` BFF surface. Mirrors the
 * Zod schemas declared in `apps/api/src/routes/companies.ts` so request and
 * response shapes never drift.
 *
 * Every wrapper goes through `fetchApiWithAuth`, which automatically attaches
 * the Bearer token, the `X-Team-Id` header (set by TeamProvider) and the
 * `X-Company-Id` header (set by CompanyProvider) for company-scoped routes.
 */
import { fetchApiBlobWithAuth, fetchApiWithAuth } from "@/lib/api";

// ── Common types ──────────────────────────────────────────────────────────

export type DianRuntimeEnv = "hab" | "prod";
export type ListStatusFilter = "active" | "archived" | "all";

export type CompanySummary = {
  id: string;
  nit: string;
  dv: string | null;
  legalName: string;
  tradeName: string | null;
  identificationType: string;
  cityCode: string | null;
  departmentCode: string | null;
  regimeCode: string | null;
  isLargeTaxpayer: boolean;
  isSelfWithholder: boolean;
  dianRuntimeEnv: DianRuntimeEnv;
  onboardingStatus: string;
  status: string;
  certUploaded: boolean;
  certUploadedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CertSoftwareMeta = {
  softwareId: string;
  hasSoftwarePin: boolean;
  testSetId?: string;
  resolutionRanges: CertResolutionRange[];
};

export type CertResolutionRange = {
  resolutionNumber: string;
  softwareId: string;
  environment: DianRuntimeEnv;
  resolutionDate?: string;
  prefix: string;
  fromNumber: number;
  toNumber: number;
  validDateFrom?: string;
  validDateTo?: string;
  technicalKeyPresent: boolean;
};

export type CertDianTenantInfo = {
  message?: string | null;
  receiverEmail?: string;
  receiverName?: string;
  statusCode?: string;
  acquirerId?: string;
  acquirerType?: string;
  fetchedAt?: string;
};

export type CertMeta = {
  nit: string;
  alias: string;
  hasPassword: boolean;
  hasCertificate: boolean;
  testSetId?: string;
  softwareInvoice: CertSoftwareMeta;
  softwarePos: CertSoftwareMeta;
  softwarePayroll: CertSoftwareMeta;
  dianTenantInfo?: CertDianTenantInfo;
};

/**
 * The natural person who signs RADIAN events for the company (PF-339) —
 * rendered as IssuerParty/Person in every event AR (DIAN AAH12–AAH16).
 */
export type RadianSignatory = {
  idType: "CC" | "CE" | "PP";
  idNumber: string;
  firstName: string;
  familyName: string;
  jobTitle?: string;
  department?: string;
};

export type CompanyDetail = CompanySummary & {
  addressLine1: string | null;
  addressLine2: string | null;
  email: string | null;
  phone: string | null;
  responsibilities: string[];
  hasLogo: boolean;
  logoUpdatedAt: string | null;
  /** Per-company FEV inbox address (PF-339) — "dale este correo a tus proveedores". */
  inboxEmail: string;
  /** Rule 8: auto-emit the acuse 030 on DIAN-validated email ingest. */
  radianAutoAcuse: boolean;
  /** Firmante de eventos RADIAN — null until configured (events blocked). */
  radianSignatory: RadianSignatory | null;
  certMeta: CertMeta | null;
  certError?: string;
};

export type Pagination = {
  total: number;
  pageSize: number;
  nextOffset: number | null;
};

export type ListCompaniesResponse = {
  companies: CompanySummary[];
  pagination: Pagination;
};

export type CreateCompanyInput = {
  nit: string;
  dv?: string;
  identificationType?: "NIT" | "CC" | "CE" | "TI" | "PP" | "PPT";
  legalName: string;
  tradeName?: string;
  cityCode?: string;
  departmentCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  email?: string;
  phone?: string;
  regimeCode?: "48" | "49";
  responsibilities?: string[];
  isLargeTaxpayer?: boolean;
  isSelfWithholder?: boolean;
};

export type UpdateCompanyInput = Partial<{
  legalName: string;
  tradeName: string | null;
  cityCode: string | null;
  departmentCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  email: string | null;
  phone: string | null;
  regimeCode: "48" | "49" | null;
  responsibilities: string[];
  isLargeTaxpayer: boolean;
  isSelfWithholder: boolean;
  radianAutoAcuse: boolean;
  /** null clears the signatory (blocks further RADIAN event emission). */
  radianSignatory: RadianSignatory | null;
}>;

export type CompanyArchived = {
  id: string;
  status: "archived";
};

export type CertDeleted = {
  message: string;
  nit: string;
};

export type DianProcess = {
  processId: string;
  operation: string;
  documentType: string;
  sourceDocumentId: string;
  idempotencyKey: string;
  status: string;
  currentStep: string | null;
  cufe: string | null;
  cude: string | null;
  cuds: string | null;
  cune: string | null;
  dianStatus: string | null;
  dianTrackId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListDianProcessesResponse = {
  processes: DianProcess[];
  pagination: Pagination;
};

// ── Wrappers ──────────────────────────────────────────────────────────────

const BASE = "/api/me/companies";

/**
 * Builds the per-request options object passing the company id via header.
 * Note: the path itself does not require the X-Company-Id header (the BFF
 * also reads `companyId` from the URL param), but we set it anyway so that
 * fetchApiWithAuth's downstream auto-injection logic is consistent.
 */
function companyHeaders(companyId: string): HeadersInit {
  return { "X-Company-Id": companyId };
}

export function listCompanies(
  params: { status?: ListStatusFilter; pageSize?: number; offset?: number } = {},
): Promise<ListCompaniesResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.pageSize !== undefined) search.set("pageSize", String(params.pageSize));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  return fetchApiWithAuth<ListCompaniesResponse>(
    qs ? `${BASE}?${qs}` : BASE,
  );
}

export function createCompany(input: CreateCompanyInput): Promise<CompanySummary> {
  return fetchApiWithAuth<CompanySummary>(BASE, {
    method: "POST",
    body: input,
  });
}

export function getCompany(companyId: string): Promise<CompanyDetail> {
  return fetchApiWithAuth<CompanyDetail>(
    `${BASE}/${encodeURIComponent(companyId)}`,
    { headers: companyHeaders(companyId) },
  );
}

export function updateCompany(
  companyId: string,
  input: UpdateCompanyInput,
): Promise<CompanySummary> {
  return fetchApiWithAuth<CompanySummary>(
    `${BASE}/${encodeURIComponent(companyId)}`,
    { method: "PATCH", body: input, headers: companyHeaders(companyId) },
  );
}

export function deleteCompany(companyId: string): Promise<CompanyArchived> {
  return fetchApiWithAuth<CompanyArchived>(
    `${BASE}/${encodeURIComponent(companyId)}`,
    { method: "DELETE", headers: companyHeaders(companyId) },
  );
}

export function deleteCert(companyId: string): Promise<CertDeleted> {
  return fetchApiWithAuth<CertDeleted>(
    `${BASE}/${encodeURIComponent(companyId)}/cert`,
    { method: "DELETE", headers: companyHeaders(companyId) },
  );
}

// ── Branding (per-company logo, PF-335) ───────────────────────────────────

export type CompanyLogoContentType = "image/png" | "image/jpeg";

export type UploadCompanyLogoInput = {
  contentBase64: string;
  contentType: CompanyLogoContentType;
};

export type CompanyLogoUpdated = {
  hasLogo: true;
  logoUpdatedAt: string;
  contentType: string;
  size: number;
};

export type CompanyLogoDeleted = {
  message: string;
  hasLogo: false;
};

/** The GET is authed (Bearer + headers), so the UI turns the Blob into an object URL. */
export function getCompanyLogoBlob(companyId: string): Promise<Blob> {
  return fetchApiBlobWithAuth(
    `${BASE}/${encodeURIComponent(companyId)}/branding/logo`,
    { headers: companyHeaders(companyId) },
  );
}

export function uploadCompanyLogo(
  companyId: string,
  input: UploadCompanyLogoInput,
): Promise<CompanyLogoUpdated> {
  return fetchApiWithAuth<CompanyLogoUpdated>(
    `${BASE}/${encodeURIComponent(companyId)}/branding/logo`,
    { method: "PUT", body: input, headers: companyHeaders(companyId) },
  );
}

export function removeCompanyLogo(companyId: string): Promise<CompanyLogoDeleted> {
  return fetchApiWithAuth<CompanyLogoDeleted>(
    `${BASE}/${encodeURIComponent(companyId)}/branding/logo`,
    { method: "DELETE", headers: companyHeaders(companyId) },
  );
}

export function listDianProcesses(
  companyId: string,
): Promise<ListDianProcessesResponse> {
  return fetchApiWithAuth<ListDianProcessesResponse>(
    `${BASE}/${encodeURIComponent(companyId)}/dian/processes`,
    { headers: companyHeaders(companyId) },
  );
}
